import React, {
  useMemo,
  useTransition,
  useCallback,
  memo,
  useEffect,
  useState,
  useRef,
} from "react";
// src/pages/FormLead.jsx
import CadeadoLead from "../Components/CadeadoLeads.jsx";

/* =========================
   Helpers locais
========================= */
function sanitizeNomeLead(txt = "") {
  return String(txt).replace(/\s+/g, " ").trim();
}
function titleCase(txt = "") {
  return String(txt)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// Formatações leves (rodar só no blur)
const formatNome = (s = "") => titleCase(sanitizeNomeLead(s));
const formatTelefone = (s = "") => {
  const dig = String(s).replace(/\D/g, "");
  if (!dig) return "";
  if (dig.length <= 10)
    return dig.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").trim();
  return dig.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").trim();
};
const parseMoedaToNumber = (s = "") => {
  const n = Number(
    String(s)
      .replace(/\s/g, "")
      .replace(/\./g, "")
      .replace(",", ".")
      .replace(/[^\d.-]/g, "")
  );
  return isNaN(n) ? "" : n;
};

/* =========================
   FastSelect (agora com "disabled")
========================= */
function FastSelect({
  name,
  label,
  value,
  onChange,
  items = [],
  getLabel = (x) => String(x?.nome ?? x?.label ?? ""),
  placeholder = "Selecione",
  className = "",
  batchSize = 200,
  startTransition,
  disabled = false, // <= NOVO
}) {
  const [focused, setFocused] = useState(false);
  const [count, setCount] = useState(0);
  const timerRef = useRef(null);

  const safeItems = useMemo(
    () => (items || []).filter((x) => x && x.id !== undefined),
    [items]
  );

  useEffect(() => {
    setCount(safeItems.length);
  }, [safeItems.length]);

  useEffect(() => {
    setCount((c) => Math.min(c || 0, safeItems.length));
  }, [safeItems.length]);

  useEffect(() => {
    if (!focused) return;
    if (count >= safeItems.length) return;

    const step = () => {
      const next = Math.min(count + batchSize, safeItems.length);
      const commit = () => setCount(next);
      if (typeof startTransition === "function") startTransition(commit);
      else commit();

      if (next < safeItems.length) {
        timerRef.current = setTimeout(step, 0);
      }
    };

    timerRef.current = setTimeout(step, 0);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [focused, count, safeItems.length, batchSize, startTransition]);

  const handleFocus = useCallback(() => {
    if (disabled) return; // <= respeita disabled
    setFocused(true);
    if (count === 0 && safeItems.length > 0)
      setCount(Math.min(batchSize, safeItems.length));
  }, [count, safeItems.length, batchSize, disabled]);

  const handleBlur = useCallback(() => {
    setFocused(false);
  }, []);

  const rendered = safeItems.slice(0, count);

  return (
    <div className="relative">
      {label && (
        <label className="absolute -top-2 left-3 bg-white px-1 text-sm font-medium text-blue-600 z-10">
          {label}
        </label>
      )}
      <select
        name={name}
        value={value == null ? "" : String(value)}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled} // <= NOVO
        className={
          className ||
          `w-full border rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none shadow-sm ${
            disabled
              ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
              : "border-blue-300 focus:ring-2 focus:ring-blue-400 bg-white"
          }`
        }
      >
        <option value="">{placeholder}</option>
        {rendered.map((item) => (
          <option key={item.id} value={String(item.id)}>
            {getLabel(item)}
          </option>
        ))}
        {focused && count < safeItems.length && (
          <option disabled>
            Carregando… ({count}/{safeItems.length})
          </option>
        )}
      </select>
    </div>
  );
}

/* =========================
   <option> memoizado (para listas pequenas)
========================= */
const Options = memo(function Options({ items, getLabel }) {
  return (
    <>
      {(items || []).map((item) => (
        <option key={item.id} value={String(item.id)}>
          {getLabel(item)}
        </option>
      ))}
    </>
  );
});

/* =========================
   Input local rápido
========================= */
function LocalInput({
  name,
  label,
  form,
  setForm,
  defaultValue = "",
  placeholder = "",
  type = "text",
  onBlurFormat,
  className = "",
  inputClassName = "",
  commitOnEnter = true,
  inputKey,
  startTransition,
}) {
  const [local, setLocal] = useState(defaultValue ?? "");

  useEffect(() => {
    setLocal(defaultValue ?? "");
  }, [defaultValue]);

  const commit = useCallback(() => {
    const raw = local;
    const val = typeof onBlurFormat === "function" ? onBlurFormat(raw) : raw;
    if (form?.[name] === val) return;

    if (typeof startTransition === "function") {
      startTransition(() => {
        setForm((prev) => ({ ...prev, [name]: val }));
      });
    } else {
      setForm((prev) => ({ ...prev, [name]: val }));
    }
  }, [local, form, name, setForm, startTransition, onBlurFormat]);

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="absolute -top-2 left-3 bg-white px-1 text-sm font-medium text-blue-600 z-10">
          {label}
        </label>
      )}
      <input
        key={inputKey}
        type={type}
        value={local}
        placeholder={placeholder}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (commitOnEnter && e.key === "Enter") {
            e.preventDefault();
            commit();
          }
        }}
        spellCheck={false}
        className={
          inputClassName ||
          "w-full border border-blue-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm"
        }
      />
    </div>
  );
}

/* =========================
   LeadForm
========================= */
function LeadForm({
  form,
  setForm,
  handleChange,
  handleSubmit,
  erros,
  empresas,
  campanhas,
  procedimentos,
  fontes,
  regioes,
  usuarios,
  dentistas,
  editandoId,

  // 👇 NOVOS (opcionais). Se não passar, busca do localStorage
  usuarioLogado,
  empresaAtiva,
}) {
  const [isPending, startTransition] = useTransition();

  // fallbacks
  const usuarioAtual =
    usuarioLogado ||
    (() => {
      try {
        return JSON.parse(localStorage.getItem("usuarioLogado") || "{}");
      } catch {
        return {};
      }
    })();

  const empresaAtual =
    empresaAtiva ||
    (() => {
      try {
        return JSON.parse(localStorage.getItem("empresaAtiva") || "{}");
      } catch {
        return {};
      }
    })();

  const empresasMemo = useMemo(
    () => (empresas || []).filter((e) => e && e.id !== undefined),
    [empresas]
  );
  const campanhasMemo = useMemo(() => campanhas || [], [campanhas]);
  const procedimentosMemo = useMemo(() => procedimentos || [], [procedimentos]);
  const fontesMemo = useMemo(() => fontes || [], [fontes]);
  const regioesMemo = useMemo(() => regioes || [], [regioes]);
  const usuariosMemo = useMemo(() => usuarios || [], [usuarios]);
  const dentistasMemo = useMemo(() => dentistas || [], [dentistas]);

  const handleSelectFast = useCallback(
    (name) => (e) => {
      const v = e.target.value;
      setForm((prev) => ({ ...prev, [name]: v })); // 👈 direto, sem atraso
    },
    [setForm]
  );

  const getEmpresaLabel = useCallback((x) => x?.nome_fantasia ?? "", []);
  const getCampanhaLabel = useCallback((x) => x?.nome ?? "", []);
  const getProcedLabel = useCallback((x) => x?.nome ?? "", []);
  const getFonteLabel = useCallback((x) => x?.nome ?? "", []);
  const getRegiaoLabel = useCallback((x) => x?.regiao ?? "", []);
  const getUsuarioLabel = useCallback((x) => x?.nome ?? "", []);
  const getDentistaLabel = useCallback((x) => x?.profissional ?? "", []);

  // 🔒 quando mudar o cadeado no componente filho, atualiza o form
  const handleCadeadoChange = useCallback(
    (novo) => {
      setForm((prev) => ({ ...prev, cadeado: Number(novo) ? 1 : 0 }));
    },
    [setForm]
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-gradient-to-br from-blue-800 via-blue-600 to-blue-500 p-6 rounded-2xl shadow-xl mb-8 space-y-6 border border-blue-700 text-white"
    >
      {/* Cabeçalho com Cadeado */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm opacity-90">
          <strong>Lead #{form?.id || "novo"}</strong>
        </div>
        {form?.id && (
          <div className="flex items-center gap-2">
            <span className="text-xs opacity-90">
              {Number(form?.cadeado) === 1 ? "Travada" : "Liberada"}
            </span>
            <CadeadoLead
              leadId={form.id}
              cadeado={Number(form.cadeado) ? 1 : 0}
              usuarioAtual={usuarioAtual}
              empresaId={empresaAtual?.id || form?.empresa_id}
              onStatusChange={handleCadeadoChange}
              // onError={(m)=> toast.error(m)}
              // onSuccess={(m)=> toast.success(m)}
              // getAuthToken={()=> localStorage.getItem("token")}
              endpointPrefix="/bloqueio-leads"
            />
          </div>
        )}
      </div>

      {/* PRIMEIRA LINHA: Empresa */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4">
          <FastSelect
            name="empresa_id"
            label="Empresa"
            value={form.empresa_id || ""}
            onChange={handleSelectFast("empresa_id")}
            items={empresasMemo}
            getLabel={getEmpresaLabel}
            placeholder="Selecione"
            startTransition={startTransition}
          />
        </div>
      </div>

      {/* SEGUNDA LINHA */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4">
          <LocalInput
            name="nome_lead"
            label="Nome do Paciente"
            form={form}
            setForm={setForm}
            defaultValue={form.nome_lead}
            placeholder="Ex.: Maria Silva"
            onBlurFormat={formatNome}
            inputKey={`nome-${editandoId ?? "novo"}`}
            startTransition={startTransition}
          />
        </div>

        <div className="col-span-2 relative">
          <label className="absolute -top-2 left-3 bg-white px-1 text-sm font-medium text-blue-600 z-10">
            Data
          </label>
          <input
            type="date"
            name="data_lead"
            value={form.data_lead || ""}
            onChange={handleChange}
            className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none shadow-sm 
            ${
              erros?.data_lead
                ? "border-red-500 focus:ring-red-500"
                : "border-blue-300 focus:ring-blue-400"
            }`}
          />
          {erros?.data_lead && (
            <p className="text-red-600 text-xs mt-1 pl-1">{erros.data_lead}</p>
          )}
        </div>

        <div className="col-span-2">
          <LocalInput
            name="telefone1"
            label="Telefone"
            form={form}
            setForm={setForm}
            defaultValue={form.telefone1}
            placeholder="(61) 9 9999-9999"
            onBlurFormat={formatTelefone}
            inputKey={`tel-${editandoId ?? "novo"}`}
            startTransition={startTransition}
          />
        </div>

        <div className="col-span-2">
          <LocalInput
            name="total_orcamento"
            label="Total Orçamento"
            form={form}
            setForm={setForm}
            defaultValue={form.total_orcamento}
            placeholder="R$ 0,00"
            onBlurFormat={parseMoedaToNumber}
            inputKey={`orc-${editandoId ?? "novo"}`}
            startTransition={startTransition}
          />
        </div>

        <div className="col-span-2">
          <FastSelect
            name="id_campanha"
            label="Campanha"
            value={form.id_campanha || ""}
            onChange={handleSelectFast("id_campanha")}
            items={campanhasMemo}
            getLabel={getCampanhaLabel}
            placeholder="Selecione"
            startTransition={startTransition}
          />
        </div>
      </div>

      {/* TERCEIRA LINHA */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4">
          <FastSelect
            name="id_procedimento"
            label="Procedimento"
            value={form.id_procedimento || ""}
            onChange={handleSelectFast("id_procedimento")}
            items={procedimentosMemo}
            getLabel={getProcedLabel}
            placeholder="Selecione"
            startTransition={startTransition}
          />
        </div>

        <div className="col-span-2">
          <FastSelect
            name="id_fonte_lead"
            label="Fonte"
            value={form.id_fonte_lead || ""}
            onChange={handleSelectFast("id_fonte_lead")}
            items={fontesMemo}
            getLabel={getFonteLabel}
            placeholder="Selecione"
            startTransition={startTransition}
          />
        </div>

        <div className="col-span-2">
          <FastSelect
            name="id_regiao"
            label="Região"
            value={form.id_regiao || ""}
            onChange={handleSelectFast("id_regiao")}
            items={regioesMemo}
            getLabel={getRegiaoLabel}
            placeholder="Selecione"
            startTransition={startTransition}
          />
        </div>

        {/* 🔒 Captador (usuario_criador_id) — TRAVADO quando cadeado = 1 */}
        <div className="col-span-2">
          <FastSelect
            name="usuario_criador_id"
            label="Captador"
            value={form.usuario_criador_id || ""}
            onChange={handleSelectFast("usuario_criador_id")}
            items={usuariosMemo}
            getLabel={getUsuarioLabel}
            placeholder="Selecione"
            startTransition={startTransition}
            disabled={Number(form?.cadeado) === 1} // <= AQUI TRAVA
          />
        </div>

        <div className="col-span-2">
          <FastSelect
            name="id_dentista"
            label="Dentista"
            value={form.id_dentista || ""}
            onChange={handleSelectFast("id_dentista")}
            items={dentistasMemo}
            getLabel={getDentistaLabel}
            placeholder="Selecione"
            startTransition={startTransition}
          />
        </div>
      </div>

      {/* Última linha */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-2 relative">
          <label className="absolute -top-2 left-3 bg-white px-1 text-sm font-medium text-blue-600 z-10">
            Potencial da Lead
          </label>
          <select
            name="potencial"
            value={form.potencial ?? ""}
            onChange={handleSelectFast("potencial")}
            className="w-full border rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm border-blue-300"
          >
            <option value="">Selecione</option>
            <option value="Alto">Alto</option>
            <option value="Médio">Médio</option>
            <option value="Baixo">Baixo</option>
          </select>
        </div>

        <div className="col-span-2 relative">
          <label className="absolute -top-2 left-3 bg-white px-1 text-sm font-medium text-blue-600 z-10">
            Status da Negociação
          </label>
          <select
            name="status_negociacao"
            value={form.status_negociacao ?? ""}
            onChange={handleSelectFast("status_negociacao")}
            className="w-full border rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 shadow-sm border-blue-300"
          >
            <option value="">Selecione</option>
            <option value="Fechado">Fechado</option>
            <option value="Fechado Parcial">Fechado Parcial</option>
            <option value="Perdido">Perdido</option>
            <option value="Negociando">Negociando</option>
            <option value="Pendente">Pendente</option>
            <option value="Prospectando">Prospectando</option>
            <option value="Migrou Concorrência">Migrou Concorrência</option>
          </select>
        </div>
      </div>
    </form>
  );
}

export default memo(LeadForm);
